import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import { styled } from "@mui/system";

import moment from "moment";
import {
  useLazyGetChatHistoryQuery,
  useLazyGetChatListQuery,
} from "../services/main";
import useAuth from "../hooks/useAuth";
import { errorToast, getFromStorage, STORAGE_KEYS } from "../helpers";
import { connectChatSocket } from "../utils/socket";
import useTranslation from "../hooks/Translation";

// Styled components
const Root = styled("div")({
  display: "flex",
  height: "100vh",
});

const Inbox = styled(Card)({
  width: "310px",
  minWidth: "310px",
  borderRadius: 5,
  display: "flex",
  flexDirection: "column",
});

const InboxHeader = styled("div")({
  padding: "16px",
  backgroundColor: "#e0e0e0",
  borderRadius: 5,
});

const InboxContent = styled("div")({
  flexGrow: 1,
  overflowY: "auto",
  padding: "8px",
});

const ConversationButton = styled(Button)({
  justifyContent: "space-between",
  textTransform: "none",
  padding: "8px",
  width: "100%",
});

const MessageArea = styled("div")({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
});

const MessageList = styled("div")({
  flexGrow: 1,
  overflowY: "auto",
  padding: "16px",
});

const MessageInput = styled("div")({
  display: "flex",
  alignItems: "center",
  padding: "16px",
});

let socket = null as any as WebSocket;

export default function ChatScreen() {
  const user = useAuth();
  const t = useTranslation();
  const getLang = getFromStorage(STORAGE_KEYS.language);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [chatList] = useLazyGetChatListQuery();
  const [chatHistory] = useLazyGetChatHistoryQuery();
  const [chatListData, setChatListData] = useState<any>([]);
  const [chatHistoryData, setChatHistoryData] = useState<any>([]);
  const [message, setMessage] = useState("");
  const [selectedId, setSelectedId] = useState("");

  const scrollToSection = () => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  //getting chat list
  const getAllChats = async () => {
    try {
      const response = await chatList({}).unwrap();
      if (response?.status === 200) {
        setChatListData(response?.data);
      } else {
        setChatListData([]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  //getting chat history
  const getChatHistory = async (session: any) => {
    try {
      const response = await chatHistory(session).unwrap();
      if (response?.status === 200) {
        setChatHistoryData(response?.data);
      } else {
        setChatHistoryData([]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  //message listener
  const listenToMessage = () => {
    socket?.addEventListener("message", (event) => {
      try {
        const parsedData = JSON.parse(event.data); // Parse the string to a JavaScript object
        console.log(parsedData, "parsedData");
        setChatHistoryData((prev: any) => [...prev, parsedData]);
        setTimeout(() => {
          scrollToSection();
        }, 500);
      } catch (error) {
        console.error("Failed to parse message data:", error);
        console.log("Raw message data:", event.data); // Keep a log of the raw data
      }
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevents adding a new line
      if (message.trim().length > 0) {
        sendMessage();
      } else {
        errorToast(t.error.pleaseWrite);
      }
    }
  };

  //connecting to socket and chat
  const handleClick = (session: string) => {
    socket?.close();
    setSelectedId(session);
    setTimeout(() => {
      scrollToSection();
    }, 500);

    socket = connectChatSocket(user?.id || "", session || "");
    if (socket) {
      listenToMessage();
    }
    getChatHistory(session);
  };

  //send message
  const sendMessage = async () => {
    setMessage("");
    socket?.send(
      JSON.stringify({
        type: "chat",
        message: message,
      })
    );
    setTimeout(() => {
      scrollToSection();
    }, 500);
  };

  useEffect(() => {
    getAllChats();
  }, []);

  return (
    <Root sx={{ maxHeight: 450, marginTop: 2 }}>
      {/* Left side: Inbox */}
      <Inbox>
        <InboxHeader>
          <Typography variant="h6" color="black">
            {t.main.inbox}
          </Typography>
        </InboxHeader>
        <InboxContent>
          {chatListData?.length ? (
            chatListData?.map((conversation: any, index: any) => (
              <ConversationButton
                key={index}
                onClick={() => handleClick(conversation?.session)}
                style={{
                  backgroundColor:
                    selectedId === conversation?.session
                      ? "rgba(43, 96, 76, 1)"
                      : "",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  {conversation?.profile_picture === "N/A" ? (
                    <Avatar sx={{ width: 40, height: 40, marginRight: 2 }}>
                      {conversation?.user_name
                        .split(" ")
                        .map((n: any) => n[0])
                        .join("")}
                    </Avatar>
                  ) : (
                    <Avatar
                      sx={{ width: 40, height: 40, marginRight: 2 }}
                      src={conversation?.profile_picture}
                    ></Avatar>
                  )}

                  <div style={{ overflow: "hidden" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        textAlign: "left",
                        color:
                          selectedId === conversation?.session ? "#fff" : "",
                      }}
                    >
                      {conversation?.user_name?.length > 20
                        ? `${conversation?.user_name.slice(0, 20)}...`
                        : conversation?.user_name}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          selectedId === conversation?.session ? "#fff" : "",
                        fontSize: 12,
                        textAlign: "start",
                      }}
                    >
                      {conversation?.latest_query?.length > 20
                        ? `${conversation?.latest_query.slice(0, 20)}...`
                        : conversation?.latest_query}
                    </Typography>
                  </div>
                </div>
                {/* <div>
                {conversation?.unread === 0 ? (
                  ""
                ) : (
                  <MDTypography>{conversation?.unread || "0"}</MDTypography>
                )}
              </div> */}
              </ConversationButton>
            ))
          ) : (
            <Typography
              variant="body1"
              color="black"
              sx={{ fontSize: 14, textAlign: "center" }}
            >
              {t.error.noChatFound}
            </Typography>
          )}
        </InboxContent>
      </Inbox>

      {/* Right side: Message Area */}
      <MessageArea>
        <MessageList>
          {/* Sample messages */}
          {chatHistoryData?.map((item: any) => {
            return (
              <div key={item?.id}>
                {item?.sender === user?.id ? (
                  <Card
                    sx={{
                      width: "fit-content",
                      maxWidth: "70%",
                      marginLeft: "auto",
                      backgroundColor: "#2B604C",
                      color: "white",
                      marginBottom: 2,
                      textAlign: "left",
                      padding: 1,
                      borderRadius: 2,
                      borderTopRightRadius: 0,
                    }}
                  >
                    <CardContent sx={{ paddingBottom: 0 }}>
                      <Typography variant="body2" color="white">
                        {item?.user_input || "-"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: 11, textAlign: "end" }}
                        color="white"
                      >
                        {moment(item.timestamp).format("h:mm A") || "-"}
                      </Typography>
                    </CardContent>
                  </Card>
                ) : (
                  <Card
                    sx={{
                      maxWidth: "70%",
                      width: "fit-content",
                      marginBottom: 2,
                      padding: 1,
                      borderRadius: 2,
                      borderTopLeftRadius: 0,
                    }}
                  >
                    <CardContent>
                      <Typography variant="body2">
                        {item?.user_input || "-"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: 11, textAlign: "end" }}
                      >
                        {moment(item.time_stamp).format("h:mm A") || "-"}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })}
          <div ref={sectionRef} />
        </MessageList>

        {chatHistoryData?.length ? (
          <MessageInput>
            <TextField
              fullWidth
              placeholder={t.main.typeMessage}
              variant="outlined"
              size="small"
              type="text"
              onKeyDown={handleKeyPress}
              value={message}
              onChange={(val: any) => setMessage(val.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#2B604C", // Default border color
                  },
                  "&:hover fieldset": {
                    borderColor: "#2B604C", // Border color on hover
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2B604C", // Border color when focused
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "black", // Placeholder color
                  },
                },
              }}
            />
            <Button
              sx={{
                marginLeft: getLang === "ar" ? 0 : 2,
                marginRight: getLang === "ar" ? 2 : 0,
                "& .MuiSvgIcon-root": {
                  transform: getLang === "ar" ? "rotate(180deg)" : "none",
                },
              }}
              variant="outlined"
              onClick={() => {
                if (message?.length) {
                  sendMessage();
                } else {
                  errorToast("Please write something");
                }
              }}
            >
              <Send />
            </Button>
          </MessageInput>
        ) : null}
      </MessageArea>
    </Root>
  );
}
